import { useState } from 'react';
import { Bookmark, BookmarkPlus, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import type { SavedView } from '@vwt/contracts';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Field,
  Input,
  Label,
} from '@vwt/ui';
import { newIdempotencyKey, useApiMutation, useApiQuery } from '../lib/api.js';
import { toastError } from '../lib/errors.js';
import { useMe } from '../lib/session.js';
import { useTranslation } from '../lib/i18n.js';

/** URL parameters that describe UI state, not the filter definition. */
const TRANSIENT = new Set(['selected', 'create', 'cursor']);

export function definitionOf(params: URLSearchParams): Record<string, string> {
  return Object.fromEntries([...params.entries()].filter(([k, v]) => !TRANSIENT.has(k) && v));
}

/**
 * Saved views store the filter definition only. Applying one replaces the current filters; the
 * list is always re-queried with the reader's own permissions.
 */
export function SavedViewsMenu({ scope, params, onApply }: { scope: SavedView['scope']; params: URLSearchParams; onApply: (query: Record<string, string>) => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const views = useApiQuery('listSavedViews', { query: { scope } });
  const remove = useApiMutation('deleteSavedView', { invalidate: ['listSavedViews'] });
  const [saving, setSaving] = useState(false);
  const current = definitionOf(params);
  const currentKey = JSON.stringify(Object.entries(current).sort());
  const list = views.data?.data ?? [];
  const mine = list.filter((v) => v.owner.id === me.id);
  const shared = list.filter((v) => v.owner.id !== me.id);
  const isActive = (v: SavedView) => JSON.stringify(Object.entries(v.query).sort()) === currentKey;
  const active = list.find(isActive);

  const item = (v: SavedView) => (
    <DropdownMenuItem key={v.id} onSelect={() => onApply(v.query)} className="group">
      <Bookmark className={isActive(v) ? 'fill-current text-accent' : undefined} />
      <span className="min-w-0 flex-1 truncate">{v.name}</span>
      {v.shared && v.owner.id === me.id ? <Users className="size-3.5 text-fg-subtle" aria-label={t('savedViews.shared')} /> : null}
      {v.owner.id === me.id ? (
        <button
          className="rounded p-0.5 text-fg-subtle opacity-0 group-hover:opacity-100 group-focus:opacity-100 hover:text-danger"
          aria-label={t('savedViews.delete')}
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            try {
              await remove.mutateAsync({ params: { id: v.id } });
              toast.success(t('savedViews.deleted'));
            } catch (error) {
              toastError(error);
            }
          }}
        >
          <Trash2 className="size-3.5" />
        </button>
      ) : (
        <span className="truncate text-xs text-fg-subtle">{v.owner.displayName}</span>
      )}
    </DropdownMenuItem>
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="md" variant="secondary" data-testid="saved-views">
            <Bookmark className={active ? 'fill-current' : undefined} /> <span className="max-w-40 truncate">{active?.name ?? t('savedViews.title')}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="max-h-[70vh] w-72 overflow-y-auto">
          {mine.length ? <DropdownMenuLabel>{t('savedViews.mine')}</DropdownMenuLabel> : null}
          {mine.map(item)}
          {shared.length ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>{t('savedViews.sharedWithEveryone')}</DropdownMenuLabel>
            </>
          ) : null}
          {shared.map(item)}
          {list.length === 0 ? <p className="px-2 py-1.5 text-sm text-fg-subtle">{t('savedViews.empty')}</p> : <DropdownMenuSeparator />}
          <DropdownMenuItem onSelect={() => setSaving(true)} disabled={Object.keys(current).length === 0}>
            <BookmarkPlus /> {t('savedViews.saveCurrent')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {saving ? <SaveViewDialog scope={scope} query={current} onClose={() => setSaving(false)} /> : null}
    </>
  );
}

function SaveViewDialog({ scope, query, onClose }: { scope: SavedView['scope']; query: Record<string, string>; onClose: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [key] = useState(newIdempotencyKey);
  const create = useApiMutation('createSavedView', { invalidate: ['listSavedViews'] });
  const save = async () => {
    try {
      await create.mutateAsync({ body: { name: name.trim(), scope, query, shared }, idempotencyKey: key });
      toast.success(t('savedViews.saved'));
      onClose();
    } catch (error) {
      toastError(error);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={t('savedViews.saveCurrent')} description={t('savedViews.hint')} closeLabel={t('common.close')}>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) void save();
          }}
        >
          <Field label={t('savedViews.name')} htmlFor="saved-view-name" required>
            <Input id="saved-view-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} autoFocus />
          </Field>
          <div className="flex items-center gap-2">
            <Checkbox id="saved-view-shared" checked={shared} onCheckedChange={(v) => setShared(v === true)} />
            <Label htmlFor="saved-view-shared">{t('savedViews.shareLabel')}</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={create.isPending} disabled={!name.trim()}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
